import React, { useState, useEffect, useMemo, useCallback, useRef, Dispatch, SetStateAction } from 'react';
import _ from 'lodash';
import * as dateFns from 'date-fns';

import List from '@/components/Icons/List';
import Users from '@/components/Icons/Users';
import X from '@/components/Icons/X';
import Modal from '@/components/Modal';
import PlusCircle from '@/components/Icons/PlusCircle';
import type { Questionnaire, QuestionnaireTemplateRawJSON } from '@/typings/Questionnaire';
import { TextArea } from '@/components/DesignLibrary/Atoms';
import classNames from 'classnames';

import Button from '@/components/DesignLibrary/Button';
import ExpandableList from '@/components/Questionnaire/ExpandableList';
import useBeforeUnload from '@/hooks/useBeforeUnload';
import {
  FieldTemplateItem,
  QuestionnaireAssigneeTemplate,
  SectionTemplate,
} from '@/typings/questionnaires-api-models';
import { downloadJsonAsFile } from '@/helpers/download';
import { nanoid } from 'nanoid';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import debounce from 'lodash/debounce';
import { useToast } from '@hooks';
import SectionEditor from '../SectionEditor';
import FormPreview from '../FormPreview';
import FieldEditor from '../FieldEditor';
import AssigneeEditor from '../AssigneeEditor';
import FormEditorProvider, { useFormEditorContext } from './FormEditorContext';
import s from './FormEditor.module.scss';
import reorderFieldTemplates from './reorderFieldTemplates';

type Props = {
  creatorId: string;
  formData: Questionnaire.Questionnaire;
  isSaving?: boolean;
  onPublish: ({ versionNote }: { versionNote: string }) => Promise<void>;
  getFieldTemplatesForSectionTemplate: (section: Pick<SectionTemplate, 'id' | 'slug' | 'questionnaireTemplateId'>) => Promise<Array<Questionnaire.Field>>;
  fieldsByKey: Record<string, Questionnaire.Field>;
  updateSectionFields: ({ sectionId, newFields }: { sectionId: string; newFields: Array<Questionnaire.Field> }) => void;
  updateTemplate: Dispatch<SetStateAction<Questionnaire.Questionnaire | undefined>>;
};

const FormEditor: React.FC<Omit<Props, 'formData' | 'fieldsByKey'>> = ({ creatorId, isSaving, onPublish, getFieldTemplatesForSectionTemplate, updateSectionFields }) => {
  const { formData, setFormData } = useFormEditorContext();
  const [currentTab, setCurrentTab] = useState<'form' | 'sections' | 'assignees'>('form');
  const [assignees, setAssignees] = useState<QuestionnaireAssigneeTemplate[]>([]);
  const [currentSection, setCurrentSection] = useState<Questionnaire.Section | null>(null);
  const [currentAssignee, setCurrentAssignee] = useState<QuestionnaireAssigneeTemplate | null>(null);
  const [currentField, setCurrentField] = useState<Questionnaire.Field | null>(null);
  const [currentFieldIsInList, setCurrentFieldIsInList] = useState(false);
  const [editor, setEditor] = useState<'section' | 'assignee' | 'field' | undefined>('section');
  const [isVersionNoteModalOpen, setIsVersionNoteModalOpen] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSavingField, setIsSavingField] = useState(false);
  const [isSavingSection , setIsSavingSection] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const { getData: updateFieldTemplate } = useNoodleApi(tsClient.questionnaires.updateFieldTemplate);
  const { getData: createFieldTemplate } = useNoodleApi(tsClient.questionnaires.createFieldTemplate);
  const { getData: deleteFieldTemplate } = useNoodleApi(tsClient.questionnaires.deleteFieldTemplate);
  const { getData: reorderSectionQuestionnaire } = useNoodleApi(tsClient.questionnaires.reorderSectionQuestionnaire);
  const { getData: reorderFieldQuestionnaire } = useNoodleApi(tsClient.questionnaires.reorderFieldQuestionnaire);
  const { getData: saveSection } = useNoodleApi(tsClient.questionnaires.saveSection, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was a error saving section',
    ],
    toastOnError: true,
  });
  const { getData: deleteSection } = useNoodleApi(tsClient.questionnaires.deleteSection);

  useBeforeUnload(hasChanges);

  useEffect(() => {
    setAssignees(formData.assigneeTemplates ?? []);

    if (formData.sections.length) {
      setCurrentSection(prev => prev ? formData.sections.find((section) => section.id === prev.id) ?? formData.sections[0] : formData.sections[0]);
    }
  }, [formData]);

  useEffect(() => {
    if (currentSection && currentSection.isLoading) {
      getFieldTemplatesForSectionTemplate(currentSection);
    }
  }, [getFieldTemplatesForSectionTemplate, currentSection]);

  useEffect(() => {
    if (currentSection?.id) {
      previewRef.current?.scrollTo({
        top: 0,
      });
    }
  }, [currentSection?.id]);

  const updateAssignee = useCallback((): void => {
    alert('Unimplemented');
  }, []);

  const updateSection = useCallback(async (
    updatedSection: Questionnaire.Section,
  ): Promise<Array<Questionnaire.Field>> => {
    setFormData(prev => prev
      ? {
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === updatedSection.id ? updatedSection : section,
        ),
      }
      : undefined);
    if (currentSection?.id === updatedSection.id) {
      setCurrentSection(updatedSection);
      setIsSavingSection(true);
      await saveSection({
        creatorId,
        data: {
          ...updatedSection,
          descriptionText: updatedSection.description,
        },
        questionnaireTemplateId: formData.id,
      });
      setIsSavingSection(false);
    }
    setHasChanges(true);

    return [];
  }, [creatorId, currentSection, formData, setFormData, saveSection]);

  const updateField = useCallback(async (updatedField: Questionnaire.Field & {
    linkedFieldTemplates: Array<{
      fieldId: string;
      fieldIndex: number | null;
      linkedFieldIndex: number | null;
    }>
  }): Promise<void> => {
    if (!currentSection) return;

    setIsSavingField(true);

    const { data: newField } = await updateFieldTemplate({
      creatorId,
      data: {
        ...updatedField,
        descriptionAST: null,
        descriptionHtml: updatedField.description,
        descriptionText: updatedField.description,
        fieldAssigneeTemplates: [],
        linkedFields: [],
        listFilterExpressionId: updatedField.listFilterExpressionId,
        questionnaireTemplateId: formData.id,
        sectionTemplateId: currentSection.id,
      } as Omit<FieldTemplateItem, 'hasPdfFills'> & {
        linkedFieldTemplates: Array<{
          fieldId: string;
          fieldIndex: number | null;
          linkedFieldIndex: number | null;
        }>;
      },
      fieldTemplateId: updatedField.id,
    });

    if (!newField || !newField.id) {
      return;
    }

    const formattedField = {
      ...newField,
      assignedTo: newField.fieldAssigneeTemplates?.map((assignee) => assignee.questionnaireAssigneeTemplateId) ?? [],
      description: newField.descriptionText ?? '',
      id: newField.id,
      listItemTemplates: updatedField.listItemTemplates,
      validation: newField.validation || undefined,
    };
    const updateNestedField = (field: Questionnaire.Field): Questionnaire.Field => ({
      ...field,
      listItemTemplates: field.listItemTemplates?.map((listItem) => listItem.id === updatedField.id
        ? updatedField
        : updateNestedField(listItem)) ?? [],
    });
    const updatedFields = currentSection.fields.map((field) => {
      if (field.id === updatedField.id) {
        return formattedField;
      }
      return updateNestedField(field);
    });

    const updatedSection = {
      ...currentSection,
      fields: updatedFields as Questionnaire.Section['fields'],
    };

    setFormData(prevState => prevState
      ? {
        ...prevState,
        sections: prevState.sections.map((section) =>
          section.id === updatedSection.id ? updatedSection : section,
        ),
      }
      : undefined);

    updateSectionFields({ newFields: updatedFields as Questionnaire.Section['fields'], sectionId: updatedSection.id });

    setIsSavingField(false);
    setHasChanges(true);
    const listField = updatedFields.find((field) => field.id === formattedField.listFieldTemplateId);
    if (listField) {
      setCurrentField(listField as Questionnaire.Field);
      setEditor('field');
    } else {
      setCurrentField(null);
    }
    setCurrentFieldIsInList(false);
  }, [creatorId, formData.id, updateFieldTemplate, currentSection, setFormData, updateSectionFields]);

  const addAssignee = useCallback((): void => {
    const newAssignee = {
      id: nanoid(),
      key: `assignee${assignees.length + 1}`,
      label: `Assignee ${assignees.length + 1}`,
      questionnaireTemplateId: formData.id,
    };

    setAssignees((prev) => [...prev, newAssignee]);
    setCurrentAssignee(newAssignee);
    setHasChanges(true);
    setEditor('assignee');
  }, [formData.id, assignees]);

  const removeAssignee = useCallback((assignee: string): void => {
    setAssignees((prev) => prev.filter((a) => a.id !== assignee));
    if (currentAssignee?.id === assignee) {
      setCurrentAssignee(null);
      setEditor(undefined);
    }
    setHasChanges(true);
  }, [currentAssignee]);

  const addSection = useCallback(async (): Promise<void> => {
    const newSection: Questionnaire.Section = {
      conditionExpression: null,
      description: '',
      fields: [],
      id: nanoid(),
      isLoading: false,
      key: `section${formData.sections.length + 1}`,
      label: 'New section',
      pdfTemplateId: null,
      questionnaireTemplateId: formData.id,
      rank: 0,
      slug: '',
      subsections: null,
    };
    await saveSection({
      creatorId,
      data: {
        ...newSection,
        descriptionText: '',
      },
      questionnaireTemplateId: formData.id,
    });
    setFormData((prev) => prev
      ? {
        ...prev,
        sections: [...prev.sections, newSection],
      }
      : undefined);
    setCurrentSection(newSection);
    setEditor('section');
    setCurrentTab('form');
  }, [setFormData, formData.sections, formData.id, setCurrentTab, creatorId, saveSection]);

  const removeSection = useCallback((sectionId: string): void => {
    setFormData((prev) => prev
      ? {
        ...prev,
        sections: prev.sections.filter((section) => section.id !== sectionId),
      }
      : undefined);
    if (currentSection?.id === sectionId) {
      setCurrentSection(null);
      setHasChanges(true);
      setCurrentField(null);
      setCurrentFieldIsInList(false);

      deleteSection({
        creatorId,
        questionnaireTemplateId: formData.id,
        sectionTemplateId: sectionId,
      });
    }
  }, [creatorId, currentSection, setFormData, deleteSection, formData.id]);

  const getUniqueFieldKey = useCallback((): string => {
    if (!currentSection) {
      return nanoid();
    }
    for (let i = 0; i < 100; i += 1) {
      const newKey = `${currentSection.key
        .replace(/[^a-zA-Z0-9_]/g, '')
        .replace(/^[0-9]+/g, '')
      }field${currentSection.fields.length + 1 + i}`;
      const existingField = currentSection.fields.find((field) => field.key === newKey);
      if (!existingField) {
        return newKey;
      }
    }
    return nanoid();
  }, [currentSection]);

  const addField = useCallback(async (parentFieldId?: string): Promise<void> => {
    if (!currentSection) return;

    const newFieldIsInList = Boolean(parentFieldId);
    const newField: Questionnaire.Field = {
      aiDeduplicationLogic: null,
      allowUnknownValues: false,
      assignedTo: [],
      autofill: null,
      conditionExpression: null,
      description: '',
      id: nanoid(),
      isEditableInTableView: false,
      isRequired: true,
      isSubtitleField: false,
      isTitleField: false,
      isViewableInTableView: true,
      isVirtual: false, // @todo - remove when this is typed better
      key: getUniqueFieldKey(),
      label: 'Label',
      linkedFieldKeys: [],
      linkedFieldTemplates: [],
      linkedFields: [],
      listFieldTemplateId: parentFieldId ?? null,
      listFilterExpression: null,
      listFilterExpressionId: null,
      listItemTemplates: [],
      listSubsection: null,
      numLinkedFields: 0,
      options: [],
      rank: currentSection.fields.filter((f) => parentFieldId ? f.listFieldTemplateId === parentFieldId : !f.listFieldTemplateId)
        .reduce((acc, val) => val.rank > acc ? val.rank : acc, 0) + 1,
      subsection: null,
      type: 'short-text',
      view: null,
    };

    const { data: savedField } = await createFieldTemplate({
      creatorId,
      data: {
        ...newField,
        descriptionAST: null,
        descriptionHtml: '',
        descriptionText: '',
        fieldAssigneeTemplates: [],
        linkedFieldTemplates: [],
        linkedFields: [],
        listFilterExpressionId : null,
        questionnaireTemplateId: formData.id,
        sectionTemplateId: currentSection.id,
        tableColumns: [],
      } as Omit<FieldTemplateItem, 'hasPdfFills'> & {
        linkedFieldTemplates: Array<{
          fieldId: string;
          fieldIndex: number | null;
          linkedFieldIndex: number | null;
        }>;
      },
    });

    if (!savedField) {
      return;
    }

    const insertNestedField = (field: Questionnaire.Field, subField: Questionnaire.Field): Questionnaire.Field => ({
      ...field,
      listItemTemplates: parentFieldId === field.id
        ? [...(field?.listItemTemplates || []), subField]
        : (field?.listItemTemplates ?? []).map((listItemTemplate) => insertNestedField(listItemTemplate, subField)),
    });

    const updatedSection = {
      ...currentSection,
      fields: [...currentSection.fields.map((f) => insertNestedField(f, savedField as unknown as Questionnaire.Field)), savedField],
    } as Questionnaire.Section;

    setFormData(prev => prev
      ? {
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === updatedSection.id ? updatedSection : section,
        ),
      }
      : undefined);

    setCurrentField({
      ...savedField,
      assignedTo: savedField.fieldAssigneeTemplates?.map((assignee) => assignee.questionnaireAssigneeTemplateId) ?? [],
      description: savedField.descriptionText ?? '',
      linkedFieldTemplates: [],
      listItemTemplates: [],
    } as Questionnaire.Field);
    setCurrentFieldIsInList(newFieldIsInList);
    setEditor('field');
  }, [createFieldTemplate, creatorId, formData.id, getUniqueFieldKey, currentSection, setFormData]);

  const handleSectionClick = useCallback((section: Questionnaire.Section): void => {
    setCurrentSection(section);
    setCurrentField(null);
    setCurrentFieldIsInList(false);
    setEditor('section');
    setCurrentTab('form');
  }, []);

  const handleAssigneeClick = useCallback((assignee: QuestionnaireAssigneeTemplate): void => {
    setCurrentAssignee(assignee);
    setEditor('assignee');
  }, []);

  const handleFieldClick = useCallback((args: { field: Questionnaire.Field; isInList: boolean }): void => {
    setCurrentField(args.field);
    setCurrentFieldIsInList(args.isInList);
    setEditor('field');
  }, []);

  const handleClickOutside = useCallback((): void => {
    setCurrentField(null);
    setCurrentFieldIsInList(false);
    setEditor('section');
  }, []);

  const removeField = useCallback(async (fieldId: string): Promise<void> => {
    if (!currentSection) return;

    const filterListItemTemplates = (field: Questionnaire.Field): Questionnaire.Field => ({
      ...field,
      listItemTemplates: (field?.listItemTemplates ?? []).filter((f) => f.id !== fieldId)
        .map(filterListItemTemplates),
    });

    const updatedSection = {
      ...currentSection,
      fields: currentSection.fields.filter((field) => field.id !== fieldId)
        .map((field) => filterListItemTemplates(field)),
    };

    updateSection(updatedSection);

    await deleteFieldTemplate({ creatorId, fieldTemplateId: fieldId });

    setHasChanges(true);
    setCurrentField(null);
    setCurrentFieldIsInList(false);
  }, [creatorId, currentSection, updateSection, deleteFieldTemplate]);

  type DebouncedReorderSectionQuestionnaire = (args: Parameters<typeof reorderSectionQuestionnaire>[0]) => Promise<void> | undefined;
  const debouncedReorderSectionQuestionnaire = useCallback<DebouncedReorderSectionQuestionnaire>(
    debounce(async (data) => {
      await reorderSectionQuestionnaire(data);
      setHasChanges(false);
    }, 1000),
    [reorderSectionQuestionnaire],
  );

  const handleReorderSections = async (newOrder: Questionnaire.Section[]): Promise<void> => {
    setFormData((prev) => prev
      ? {
        ...prev,
        sections: newOrder,
      }
      : undefined);
    setHasChanges(true);
    await debouncedReorderSectionQuestionnaire({
      order: newOrder.map((section,index) => ({
        rank: index + 1,
        sectionId: section.id,
      })),
      questionnaireTemplateId:formData.id,
    });
  };

  type ReorderFieldQuestionnaire = (args: Parameters<typeof reorderFieldQuestionnaire>[0]) => Promise<void> | undefined;
  const debounceReorderFieldQuestionnaire = useCallback<ReorderFieldQuestionnaire>(
    debounce(async ({ questionnaireTemplateId, order }) => {
      await reorderFieldQuestionnaire({ order, questionnaireTemplateId });
      setHasChanges(false);
    }, 1_000),
    // create new debounced function if the section changes so that we save the new order for a section even after switching
    [reorderFieldQuestionnaire, currentSection?.id],
  );

  const handleReorderFields: NonNullable<Parameters<typeof FormPreview>[0]['onReorderFields']> = ({ item, direction }) => {
    if (!currentSection) {
      return;
    }

    const sortedItems = reorderFieldTemplates<(typeof currentSection.fields)[number]>({
      allItems: currentSection.fields,
      direction,
      item,
    });

    if (item.listFieldTemplateId) {
      const parentField = sortedItems.find((field) => field.id === item.listFieldTemplateId);
      if (parentField) {
        parentField.listItemTemplates = sortedItems.filter((field) => field.listFieldTemplateId === parentField.id);
      }
    }

    setFormData((prev) => prev
      ? {
        ...prev,
        sections: prev.sections.map((section) => (section.id === currentSection.id
          ? {
            ...section,
            fields: sortedItems,
          }
          : section
        )),
      }
      : undefined);

    setHasChanges(true);
    debounceReorderFieldQuestionnaire({
      order: sortedItems.map((field) => ({
        fieldId: field.id,
        rank: field.rank,
      })),
      questionnaireTemplateId: formData.id,
    });

  };

  const handlePublish = useCallback((): void => {
    setIsVersionNoteModalOpen(true);
  }, []);

  const handleConfirmPublish = useCallback(async (): Promise<void> => {
    await onPublish({ versionNote });
    setHasChanges(false);
    setIsVersionNoteModalOpen(false);
  }, [onPublish, versionNote]);

  const handleCloseModal = useCallback((): void => {
    setVersionNote('');
    setIsVersionNoteModalOpen(false);
  }, []);

  const usedSlugs = useMemo(
    () =>
      (formData.sections ?? [])
        .filter((section: Questionnaire.Section) => section.id !== currentSection?.id)
        .map((section: Questionnaire.Section) => section.slug)
        .filter(Boolean),
    [formData, currentSection],
  );

  const usedAssigneeLabels = useMemo(
    () => assignees.map(({ id, label }) => (id === currentAssignee?.id ? '' : label)),
    [assignees, currentAssignee],
  );

  const usedSectionKeys = useMemo(
    () =>
      (formData.sections ?? [])
        .filter((section: Questionnaire.Section) => section.id !== currentSection?.id)
        .map((section: Questionnaire.Section) => section.key)
        .filter(Boolean),
    [formData, currentSection],
  );

  const usedAssigneeKeys = useMemo(
    () => assignees.map(({ id, key }) => (id === currentAssignee?.id ? '' : key)),
    [assignees, currentAssignee],
  );

  const usedFieldKeys = useMemo(
    () =>
      (formData.sections ?? [])
        .reduce((p: Questionnaire.Field[], section: Questionnaire.Section) => [...p, ...section.fields], [])
        .map((field: Questionnaire.Field) => (field.id === currentField?.id ? '' : field.key))
        .filter(Boolean),
    [formData, currentField],
  );

  const handleAddField = (parentFieldId?: string): void => {
    addField(parentFieldId);

    if (!parentFieldId) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleExportJSON = useCallback((): void => {
    const jsonData: QuestionnaireTemplateRawJSON = {
      description: formData.description,
      label: formData.label,
      sections: formData.sections.map((section) => ({
        description: section.description,
        fields: section.fields.map((field) => ({
          ..._.pick(
            field,
            'key',
            'label',
            'description',
            'type',
            'hint',
            'placeholder',
            'rank',
            'options',
            'view',
            'referencedList',
          ),
          assignees: field.assignedTo.map((assigneeId) => assignees.find(({ id }) => id === assigneeId))
            .filter(Boolean)
            .map((assignee) => ({ key: assignee!.key, label: assignee!.label })),
          optional: !field.isRequired,
        })),
        key: section.key,
        label: section.label,
      })),
    };

    downloadJsonAsFile<QuestionnaireTemplateRawJSON>(
      jsonData,
      `${jsonData.label}-v${formData.version} (${dateFns.format(new Date(), 'MM-dd-yyyy')})`,
    );
  }, [formData, assignees]);

  return (
    <div className={s.formEditor}>
      <div className={s.header}>
        <div className={s.info}>
          <div className={s.title}>{formData.label}</div>
          <div className={s.description}>{formData.description}</div>
        </div>
        <div className={s.headerActions}>
          <Button loading={isSaving} variant='primary' size='sm' onClick={handlePublish}>Publish</Button>
          <Button variant='secondary' size='sm' onClick={handleExportJSON}>Export as JSON</Button>
          <div className={s.versionInfo}>
            <div>Version: {formData.version} {formData.versionNote && `(${formData.versionNote})`}</div>
            <div>Updated by {formData?.updatedByName ?? 'N/A'}</div>
          </div>
        </div>
      </div>
      <div className={s.controls}>
        <div>
          <Button variant='secondary' size='sm' onClick={() => { setEditor(undefined); setCurrentTab(currentTab === 'sections' ? 'form' : 'sections'); }} className={classNames((currentTab === 'sections' || currentTab === 'form') && s.selectedTab)}>
            {currentTab === 'sections' ? <X size={16} color="var(--color-gray-100)" /> : <List size={16} color="var(--color-gray-100)" />}
            Sections: <b>{currentSection?.label}</b>
          </Button>
          <Button variant='secondary' size='sm' onClick={() => { setEditor(undefined); setCurrentTab(currentTab === 'assignees' ? 'form' : 'assignees'); }} className={classNames((currentTab === 'assignees') && s.selectedTab)}>
            {currentTab === 'assignees' ? <X size={16} color="var(--color-gray-100)" /> : <Users size={16} color="var(--color-gray-100)" />}
            Assignees ({assignees.length})
          </Button>
        </div>
        <Button variant='secondary' size='sm' className={s.addField} onClick={() => {
          handleAddField();
          setCurrentTab('form');
        }}>
          <PlusCircle size={16} color="var(--color-noodle)" weight="fill" />
          Add Field
        </Button>
      </div>
      <div className={s.body}>
        {currentTab === 'form' && <>
          <div className={s.clickCatcher} onClick={handleClickOutside} />
          <div className={s.editorPane}>
            {editor === 'section' && currentSection && (
              <SectionEditor
                section={currentSection}
                usedSlugs={usedSlugs}
                usedKeys={usedSectionKeys}
                isSaving={isSavingSection}
                onUpdateSection={updateSection}
                onRemoveSection={removeSection}
              />
            )}

            {editor === 'field' && currentSection && currentField && (
              <FieldEditor
                isSaving={isSavingField}
                currentSection={currentSection}
                field={currentField}
                usedKeys={usedFieldKeys}
                onUpdateField={updateField}
                onRemoveField={removeField}
                assigneeTemplates={assignees}
                onAddField={handleAddField}
                isInList={currentFieldIsInList}
                formData={formData}
                canSave={!isSaving}
              />
            )}
          </div>
          <div className={s.previewPane} ref={previewRef}>
            {currentSection
              ? (
                <FormPreview
                  ref={bottomRef}
                  formData={formData}
                  assignees={assignees}
                  currentSection={currentSection}
                  currentField={currentField?.id}
                  onSelectField={handleFieldClick}
                  onReorderFields={handleReorderFields}
                />
              )
              : null}
          </div>
        </>}

        {currentTab === 'sections' && <ExpandableList<Questionnaire.Section>
          initOpen
          items={formData.sections}
          label="Sections"
          headerIcon={<List />}
          currentSelectedItem={currentSection?.id}
          onAddItem={addSection}
          onReorderItems={handleReorderSections}
          onSelectItem={handleSectionClick}
        />}

        {currentTab === 'assignees' && <ExpandableList<QuestionnaireAssigneeTemplate>
          items={assignees}
          label="Assignees"
          headerIcon={<Users />}
          currentSelectedItem={currentAssignee?.id}
          onAddItem={addAssignee}
          onSelectItem={handleAssigneeClick}
        />}
      </div>
      {isVersionNoteModalOpen && (
        <Modal
          title="Publish Questionnaire"
          onClose={() => setIsVersionNoteModalOpen(false)}
        >
          <form className={s.versionNoteForm}>
            <TextArea
              label=""
              description="Version note"
              value={versionNote}
              onChange={setVersionNote}
            />
            <div className={s.modalButtons}>
              <Button loading={isSaving} size="sm" variant="primary" onClick={handleConfirmPublish}>Publish</Button>
              <Button size="sm" variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            </div>
          </form>
        </Modal>
      )}
      {editor === 'assignee' && currentAssignee && (
        <Modal title="Assignee" onClose={() => setEditor(undefined)}>
          <AssigneeEditor
            usedLabels={usedAssigneeLabels}
            assignee={currentAssignee}
            usedKeys={usedAssigneeKeys}
            onUpdateAssignee={updateAssignee}
            onRemoveAssignee={removeAssignee}
          />
        </Modal>
      )}
    </div>
  );
};

const FormEditorContainer: React.FC<Props> = ({
  creatorId,
  getFieldTemplatesForSectionTemplate,
  formData,
  isSaving,
  onPublish,
  fieldsByKey,
  updateSectionFields,
  updateTemplate,
}) => (
  <FormEditorProvider
    creatorId={creatorId}
    formData={formData}
    fieldsByKey={fieldsByKey}
    updateTemplate={updateTemplate}
  >
    <FormEditor
      onPublish={onPublish}
      creatorId={creatorId}
      getFieldTemplatesForSectionTemplate={getFieldTemplatesForSectionTemplate}
      updateSectionFields={updateSectionFields}
      isSaving={isSaving}
      updateTemplate={updateTemplate}
    />
  </FormEditorProvider>
);

export default FormEditorContainer;
